import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { dramas, dramaRegions, dramaTypes } from "@/mocks/dramas";
import { actors } from "@/mocks/actors";
import DramaCard from "@/components/feature/DramaCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import CardSkeleton from "@/components/feature/CardSkeleton";
import Pagination from "@/components/feature/Pagination";
import usePageMeta from "@/hooks/usePageMeta";

const PAGE_SIZE = 12;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(query);
  const [types, setTypes] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  usePageMeta(
    query ? `“${query}”的搜索结果 - 善良的秘书` : "搜索剧集 - 善良的秘书",
    query
      ? `善良的秘书为你找到与“${query}”相关的电视剧与演员信息，支持按类型和地区进一步筛选。`
      : "输入剧名、演员或关键词，快速查找你想看的电视剧。"
  );

  useEffect(() => {
    setKeyword(query);
    setPage(1);
  }, [query]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = dramas.filter((d) => {
      const actorNames = d.cast
        .map((cid) => actors.find((a) => a.id === cid)?.name || "")
        .join(" ");
      const haystack = `${d.title} ${d.types.join(" ")} ${d.director} ${d.region} ${actorNames}`.toLowerCase();
      const matchKeyword = q === "" || haystack.includes(q);
      const matchType = types.length === 0 || d.types.some((t) => types.includes(t));
      const matchRegion = regions.length === 0 || regions.includes(d.region);
      return matchKeyword && matchType && matchRegion;
    });

    if (q === "") {
      list = [...list].sort((a, b) => b.hot - a.hot);
    }
    return list;
  }, [query, types, regions]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(timer);
  }, [query, types, regions, page]);

  useEffect(() => {
    setPage(1);
  }, [types, regions]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const pageItems = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setSearchParams(q ? { q } : {}, { replace: true });
  };

  const clearAll = () => {
    setKeyword("");
    setTypes([]);
    setRegions([]);
    setSearchParams({}, { replace: true });
  };

  const hotRecommend = [...dramas].sort((a, b) => b.hot - a.hot).slice(0, 4);
  const matchedActors = query.trim()
    ? actors.filter((a) => a.name.toLowerCase().includes(query.trim().toLowerCase()))
    : [];

  return (
    <div className="w-full px-4 md:px-6 py-8 md:py-12">
      <Breadcrumb crumbs={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />

      <div className="mt-4 mb-6 max-w-3xl">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">搜索剧集</h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600">
          支持剧名、演员名与关键词模糊匹配（前端演示，接口待接入）。
        </p>
      </div>

      <form
        onSubmit={submit}
        className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3 md:p-4"
      >
        <div className="flex flex-1 items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
          <i className="ri-search-line text-base text-foreground-500"></i>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="输入剧名、演员或关键词"
            aria-label="搜索关键词"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
          {keyword && (
            <button
              type="button"
              onClick={() => setKeyword("")}
              aria-label="清空关键词"
              className="text-foreground-400 hover:text-foreground-700 cursor-pointer"
            >
              <i className="ri-close-circle-fill"></i>
            </button>
          )}
        </div>
        <button
          type="submit"
          className="flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
        >
          <i className="ri-search-line"></i>
          搜索
        </button>
      </form>

      <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-3 md:p-4 space-y-3">
        <SimpleFilter label="类型" options={dramaTypes} selected={types} onToggle={(v) => toggle(types, v, setTypes)} />
        <SimpleFilter label="地区" options={dramaRegions} selected={regions} onToggle={(v) => toggle(regions, v, setRegions)} />
      </div>

      {query && (
        <p className="mt-6 text-sm text-foreground-600">
          共找到
          <strong className="mx-1 font-label text-primary-600">{results.length}</strong>
          条与
          <strong className="mx-1 text-foreground-900">“{query}”</strong>
          相关的结果
        </p>
      )}

      {matchedActors.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground-500">相关演员：</span>
          {matchedActors.map((a) => (
            <Link
              key={a.id}
              to={`/actor/${a.id}`}
              className="flex items-center gap-2 rounded-full border border-background-200 bg-background-50 py-1 pl-1 pr-3 hover:border-primary-300 transition-colors"
            >
              <span className="h-6 w-6 overflow-hidden rounded-full">
                <img src={a.avatar} alt={`${a.name} 头像`} className="h-full w-full object-cover object-top" />
              </span>
              <span className="text-sm text-foreground-800">{a.name}</span>
            </Link>
          ))}
        </div>
      )}

      <div className="mt-6">
        {loading ? (
          <CardSkeleton count={8} />
        ) : pageItems.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {pageItems.map((d) => (
              <DramaCard key={d.id} drama={d} />
            ))}
          </div>
        ) : (
          <div>
            <EmptyState
              icon="ri-search-eye-line"
              title="没有找到匹配的剧集"
              description="试试更换关键词，或清空筛选条件。下面是当前热度最高的剧集，或许你会感兴趣。"
              action={
                <button
                  type="button"
                  onClick={clearAll}
                  className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 whitespace-nowrap cursor-pointer hover:border-primary-300 hover:text-primary-600 transition-colors"
                >
                  <i className="ri-refresh-line"></i>
                  清空搜索与筛选
                </button>
              }
            />
            <div className="mt-8">
              <h2 className="mb-4 font-heading text-base font-bold text-foreground-950">热门剧集推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {hotRecommend.map((d) => (
                  <DramaCard key={d.id} drama={d} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {!loading && pageItems.length > 0 && (
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      )}
    </div>
  );
}

function toggle<T>(list: T[], value: T, setter: (v: T[]) => void) {
  setter(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
}

function SimpleFilter<T extends string | number>({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: T[];
  selected: T[];
  onToggle: (v: T) => void;
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
      <span className="w-12 shrink-0 pt-1 text-sm text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={String(o)}
            type="button"
            onClick={() => onToggle(o)}
            className={`rounded-md border px-3 py-1.5 text-sm whitespace-nowrap cursor-pointer transition-colors ${
              selected.includes(o)
                ? "border-primary-300 bg-primary-50 text-primary-700"
                : "border-background-200 text-foreground-700 hover:border-primary-200 hover:bg-background-100"
            }`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}